import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  ClientBilling,
  AgencyBillingSummary,
  UsageChartData,
  MarkupConfig,
  Invoice,
  BillingFilters,
} from "@/types/billing";

/**
 * Hook for agency billing data and operations
 *
 * Provides:
 * - Client billing overview
 * - Usage data and charts
 * - Markup configuration
 * - Invoice generation
 */
export function useAgencyBilling(filters?: BillingFilters) {
  const { activeTenantId, isAgency } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all client billing data for the agency
  const clientBillingQuery = useQuery({
    queryKey: ["agency-billing", "clients", activeTenantId, filters],
    queryFn: async (): Promise<ClientBilling[]> => {
      const { data, error } = await supabase.rpc("calie_get_agency_client_billing", {
        p_period_start: filters?.period_start || null,
        p_period_end: filters?.period_end || null,
      });

      if (error) {
        if (error.code === "42883") {
          console.warn("calie_get_agency_client_billing RPC not yet implemented");
          return [];
        }
        throw error;
      }

      // Map the RPC response to ClientBilling type
      return ((data || []) as Array<{
        id: string;
        tenant_id: string;
        tenant_name: string;
        base_cost: number;
        markup_percentage: number;
        markup_amount: number;
        total_billed: number;
        unbilled_amount: number;
        last_invoice_date: string | null;
        status: string;
        voice_minutes: number;
        voice_cost: number;
        sms_count: number;
        sms_cost: number;
        transcription_chars: number;
        transcription_cost: number;
        ai_requests: number;
        ai_cost: number;
        total_cost: number;
      }>).map(row => ({
        id: row.id,
        tenant_id: row.tenant_id,
        tenant_name: row.tenant_name,
        base_cost: row.base_cost,
        markup_percentage: row.markup_percentage,
        markup_amount: row.markup_amount,
        total_billed: row.total_billed,
        unbilled_amount: row.unbilled_amount,
        last_invoice_date: row.last_invoice_date,
        status: row.status as 'current' | 'overdue' | 'pending',
        usage_summary: {
          tenant_id: row.tenant_id,
          tenant_name: row.tenant_name,
          voice_minutes: row.voice_minutes,
          voice_cost: row.voice_cost,
          sms_count: row.sms_count,
          sms_cost: row.sms_cost,
          transcription_chars: row.transcription_chars,
          transcription_cost: row.transcription_cost,
          ai_requests: row.ai_requests,
          ai_cost: row.ai_cost,
          total_cost: row.total_cost,
          period_start: filters?.period_start || '',
          period_end: filters?.period_end || '',
        }
      }));
    },
    enabled: !!activeTenantId && isAgency(),
  });

  // Fetch agency billing summary
  const summaryQuery = useQuery({
    queryKey: ["agency-billing", "summary", activeTenantId, filters],
    queryFn: async (): Promise<AgencyBillingSummary | null> => {
      const { data, error } = await supabase.rpc("calie_get_agency_billing_summary", {
        p_period_start: filters?.period_start || null,
        p_period_end: filters?.period_end || null,
      });

      if (error) {
        if (error.code === "42883") {
          console.warn("calie_get_agency_billing_summary RPC not yet implemented");
          return null;
        }
        throw error;
      }

      // RPC returns array, get first row
      const row = Array.isArray(data) ? data[0] : data;
      return row as AgencyBillingSummary;
    },
    enabled: !!activeTenantId && isAgency(),
  });

  // Fetch usage chart data
  const usageChartQuery = useQuery({
    queryKey: ["agency-billing", "usage-chart", activeTenantId, filters],
    queryFn: async (): Promise<UsageChartData[]> => {
      const { data, error } = await supabase.rpc("calie_get_agency_usage_chart", {
        p_period_start: filters?.period_start || null,
        p_period_end: filters?.period_end || null,
        p_granularity: 'day',
      });

      if (error) {
        if (error.code === "42883") {
          console.warn("calie_get_agency_usage_chart RPC not yet implemented");
          return [];
        }
        throw error;
      }

      return (data || []) as UsageChartData[];
    },
    enabled: !!activeTenantId && isAgency(),
  });

  // Fetch markup configuration - use direct table query
  const markupConfigQuery = useQuery({
    queryKey: ["agency-billing", "markup", activeTenantId],
    queryFn: async (): Promise<MarkupConfig[]> => {
      if (!activeTenantId) return [];
      
      const { data, error } = await supabase
        .from("calie_agency_markup_config")
        .select("*")
        .eq("agency_id", activeTenantId)
        .order("created_at", { ascending: false });

      if (error) {
        if (error.code === "42P01") {
          console.warn("calie_agency_markup_config table not yet created");
          return [];
        }
        throw error;
      }

      return (data || []) as MarkupConfig[];
    },
    enabled: !!activeTenantId && isAgency(),
  });

  // Update markup configuration
  const updateMarkupMutation = useMutation({
    mutationFn: async (config: Partial<MarkupConfig> & { tenant_id?: string | null }) => {
      const { error } = await supabase.rpc("calie_update_markup_config", {
        p_tenant_id: config.tenant_id || null,
        p_markup_percentage: config.markup_percentage || null,
        p_voice_markup: config.voice_markup || null,
        p_sms_markup: config.sms_markup || null,
        p_transcription_markup: config.transcription_markup || null,
        p_ai_markup: config.ai_markup || null,
      });

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-billing", "markup"] });
      queryClient.invalidateQueries({ queryKey: ["agency-billing", "clients"] });
    },
  });

  // Delete markup configuration (reset to inherited)
  const deleteMarkupMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      if (!activeTenantId) throw new Error("No active tenant");

      const { error } = await supabase
        .from("calie_agency_markup_config")
        .delete()
        .eq("agency_id", activeTenantId)
        .eq("tenant_id", tenantId);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-billing", "markup"] });
      queryClient.invalidateQueries({ queryKey: ["agency-billing", "clients"] });
    },
  });

  // Generate invoice for a client
  const generateInvoiceMutation = useMutation({
    mutationFn: async (params: { tenant_id: string; period_start: string; period_end: string }) => {
      const { data, error } = await supabase.rpc("calie_generate_client_invoice", {
        p_tenant_id: params.tenant_id,
        p_period_start: params.period_start,
        p_period_end: params.period_end,
      });

      if (error) throw error;
      
      // RPC returns array, get first row
      const row = Array.isArray(data) ? data[0] : data;
      // Map RPC response to Invoice type
      return {
        id: (row as any).id,
        tenant_id: (row as any).tenant_id,
        tenant_name: (row as any).tenant_name,
        invoice_number: (row as any).invoice_number,
        amount: (row as any).amount,
        base_amount: (row as any).base_amount,
        markup_amount: (row as any).markup_amount,
        status: (row as any).status,
        period_start: (row as any).period_start,
        period_end: (row as any).period_end,
        due_date: (row as any).due_date,
        paid_date: (row as any).paid_date,
        line_items: Array.isArray((row as any).line_items) ? (row as any).line_items : [],
        created_at: (row as any).created_at || new Date().toISOString(),
      } as Invoice;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-billing"] });
    },
  });

  // Generate batch invoices for all clients
  const generateBatchInvoicesMutation = useMutation({
    mutationFn: async (params: { tenant_ids: string[]; period_start: string; period_end: string }) => {
      const { data, error } = await supabase.rpc("calie_generate_batch_invoices", {
        p_tenant_ids: params.tenant_ids,
        p_period_start: params.period_start,
        p_period_end: params.period_end,
      });

      if (error) throw error;
      return (data || []) as Invoice[];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-billing"] });
    },
  });

  return {
    // Data
    clients: clientBillingQuery.data || [],
    summary: summaryQuery.data,
    usageChart: usageChartQuery.data || [],
    markupConfigs: markupConfigQuery.data || [],

    // Loading states
    isLoading:
      clientBillingQuery.isLoading ||
      summaryQuery.isLoading ||
      usageChartQuery.isLoading,
    isMarkupLoading: markupConfigQuery.isLoading,

    // Error states
    error: clientBillingQuery.error || summaryQuery.error || usageChartQuery.error,

    // Mutations
    updateMarkup: updateMarkupMutation.mutate,
    isUpdatingMarkup: updateMarkupMutation.isPending,

    deleteMarkup: deleteMarkupMutation.mutate,
    isDeletingMarkup: deleteMarkupMutation.isPending,

    generateInvoice: generateInvoiceMutation.mutate,
    isGeneratingInvoice: generateInvoiceMutation.isPending,

    generateBatchInvoices: generateBatchInvoicesMutation.mutate,
    isGeneratingBatchInvoices: generateBatchInvoicesMutation.isPending,

    // Refetch
    refetch: () => {
      clientBillingQuery.refetch();
      summaryQuery.refetch();
      usageChartQuery.refetch();
    },
  };
}

/**
 * Hook for fetching a single client's billing details
 */
export function useClientBilling(clientTenantId: string | null) {
  const { activeTenantId, isAgency } = useAuth();

  const clientDetailQuery = useQuery({
    queryKey: ["agency-billing", "client-detail", clientTenantId],
    queryFn: async (): Promise<ClientBilling | null> => {
      if (!clientTenantId) return null;

      // Use the main client billing RPC with tenant filter
      const { data, error } = await supabase.rpc("calie_get_agency_client_billing", {
        p_period_start: null,
        p_period_end: null,
      });

      if (error) {
        if (error.code === "42883") {
          console.warn("calie_get_agency_client_billing RPC not yet implemented");
          return null;
        }
        throw error;
      }

      // Filter to find the specific client
      const clients = data as Array<{
        id: string;
        tenant_id: string;
        tenant_name: string;
        base_cost: number;
        markup_percentage: number;
        markup_amount: number;
        total_billed: number;
        unbilled_amount: number;
        last_invoice_date: string | null;
        status: string;
        voice_minutes: number;
        voice_cost: number;
        sms_count: number;
        sms_cost: number;
        transcription_chars: number;
        transcription_cost: number;
        ai_requests: number;
        ai_cost: number;
        total_cost: number;
      }>;
      
      const row = clients?.find(c => c.tenant_id === clientTenantId);
      if (!row) return null;

      return {
        id: row.id,
        tenant_id: row.tenant_id,
        tenant_name: row.tenant_name,
        base_cost: row.base_cost,
        markup_percentage: row.markup_percentage,
        markup_amount: row.markup_amount,
        total_billed: row.total_billed,
        unbilled_amount: row.unbilled_amount,
        last_invoice_date: row.last_invoice_date,
        status: row.status as 'current' | 'overdue' | 'pending',
        usage_summary: {
          tenant_id: row.tenant_id,
          tenant_name: row.tenant_name,
          voice_minutes: row.voice_minutes,
          voice_cost: row.voice_cost,
          sms_count: row.sms_count,
          sms_cost: row.sms_cost,
          transcription_chars: row.transcription_chars,
          transcription_cost: row.transcription_cost,
          ai_requests: row.ai_requests,
          ai_cost: row.ai_cost,
          total_cost: row.total_cost,
          period_start: '',
          period_end: '',
        }
      };
    },
    enabled: !!activeTenantId && !!clientTenantId && isAgency(),
  });

  const invoicesQuery = useQuery({
    queryKey: ["agency-billing", "client-invoices", clientTenantId],
    queryFn: async (): Promise<Invoice[]> => {
      if (!clientTenantId) return [];

      const { data, error } = await supabase
        .from("calie_invoices")
        .select("*")
        .eq("tenant_id", clientTenantId)
        .order("created_at", { ascending: false });

      if (error) {
        if (error.code === "42P01") {
          console.warn("calie_invoices table not yet created");
          return [];
        }
        throw error;
      }

      // Map to Invoice type
      return (data || []).map(inv => ({
        id: inv.id,
        tenant_id: inv.tenant_id,
        invoice_number: inv.invoice_number,
        amount: inv.total,
        base_amount: inv.subtotal,
        markup_amount: inv.tax, // Using tax field for markup
        status: inv.status as Invoice['status'],
        period_start: inv.billing_period_start,
        period_end: inv.billing_period_end,
        due_date: inv.due_date,
        paid_date: inv.paid_at,
        line_items: [],
        created_at: inv.created_at,
      }));
    },
    enabled: !!clientTenantId && isAgency(),
  });

  return {
    client: clientDetailQuery.data,
    invoices: invoicesQuery.data || [],
    isLoading: clientDetailQuery.isLoading || invoicesQuery.isLoading,
    error: clientDetailQuery.error || invoicesQuery.error,
  };
}
